import { Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseHighchart from '../BaseHighchart';
import {
  ChartDataType,
} from '../types';
import { DEFAULT_IRRIGATION_THRESHOLD, DEFAULT_ROOTS_THRESHOLD, prepareData } from './builder';

import { ROOTS_NAME, CHART_TITLE_PROFUNDIDAD_RIEGO } from '../../../constants/graphs';
import apiClient from '../../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../../requests/api/apiTypes';
import { ReduxState } from '../../../types';
import { navigateToChart } from '../../../utils/helpers';
import { FetchError } from '../../../views/types';
import ChartFilter from '../ChartFilter';
import ConversationModal from '../ConversationTab/conversationModal';
import { getConversationTooltip } from '../LineCharts/utils';
import { getPointWidth, getRootsChartConfig } from '../utils';
import ChartConfigDialog from '../ChartConfigDialog/ChartConfigDialog';
import RootsConfigContent from './RootsConfigContent';
import useChartConfigClick from '../../../hooks/useChartConfigClick';

function RootsChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  height = 450,
  title = CHART_TITLE_PROFUNDIDAD_RIEGO,
  yAxisTitle = 'Profundidad (cm)',
  thresholds,
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  height?: number | string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  yAxisTitle?: string
  title?: string
  thresholds?: {
    irrigationThreshold: number;
    rootsThreshold: number;
  }
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
}) {
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    absoluteCategories: [],
  });
  const [irrigationTimes, setIrrigationTimes] = useState<string[]>([]);
  const [internalThresholds, setInternalThresholds] = useState<{
    irrigationThreshold: number;
    rootsThreshold: number;
  } | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();
  const [maxDetectionValue, setMaxDetectionValue] = useState<number>(0);
  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);
  const [isInitialConfigLoaded, setIsInitialConfigLoaded] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const columnWidth = useMemo(() => {
    if (!interval?.startDate || !interval?.endDate) {
      return 15;
    }
    return getPointWidth(interval.startDate, interval.endDate);
  }, [interval]);
  const { enqueueSnackbar } = useSnackbar();

  const chartName = 'profundidad-riego';

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const preparedFilters = useMemo(() => {
    if (!interval) {
      return undefined;
    }
    return {
      farmId,
      startDate: interval.startDate,
      endDate: interval.endDate,
    };
  }, [farmId, interval]);

  useEffect(() => {
    const loadInitialConfig = async () => {
      if (interval) {
        try {
          const config = await apiClient.rootsConfig.rootsConfigDetail(sectorId);
          setInternalThresholds({
            irrigationThreshold: config.data.rootsConfig?.irrigationThreshold
              || DEFAULT_IRRIGATION_THRESHOLD,
            rootsThreshold: config.data.rootsConfig?.rootsThreshold || DEFAULT_ROOTS_THRESHOLD,
          });
        } catch (error) {
          setInternalThresholds({
            irrigationThreshold: DEFAULT_IRRIGATION_THRESHOLD,
            rootsThreshold: DEFAULT_ROOTS_THRESHOLD,
          });
        } finally {
          setIsInitialConfigLoaded(true);
        }
      }
    };
    loadInitialConfig();
  }, [interval]);

  const createSeriesData = useCallback((preparedData: any) => [
    {
      name: 'Profundidad de riego (cm)',
      type: 'column',
      data: preparedData.irrigationSeries.series,
      marker: {
        enabled: false,
      },
      color: '#2374BF',
      pointWidth: columnWidth,

    },
    {
      name: 'Actividad de raíces (cm)',
      type: 'column',
      data: preparedData.rootsSeries.series,
      marker: {
        enabled: false,
      },
      color: '#D2B48C', // Light brown color
      pointWidth: columnWidth,
    },
  ], [interval]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        if (isInitialConfigLoaded) {
          const preparedData = await prepareData({
            ...preparedFilters,
            farmId,
            sectorId,
            startDate: interval?.startDate || '',
            endDate: interval?.endDate || '',
            irrigationThreshold: thresholds?.irrigationThreshold
              || internalThresholds?.irrigationThreshold || DEFAULT_IRRIGATION_THRESHOLD,
            rootsThreshold: thresholds?.rootsThreshold
              || internalThresholds?.rootsThreshold || DEFAULT_ROOTS_THRESHOLD,
            telemetryType: selectedSector?.telemetryType || 'wiseconn',
            sn: selectedSector?.sn || '',
            chart: ROOTS_NAME,
          });

          // Calculate max detection value
          const maxDetection = Math.max(
            preparedData.rootsSeries.maxValue,
            preparedData.irrigationSeries.maxValue,
          );
          setMaxDetectionValue(maxDetection + 10);
          // Extract irrigation times from irrigationDetection and filter out undefined values
          const times = preparedData.irrigationDetection
            .map((item) => item.irrigationTime)
            .filter((time): time is string => time !== undefined);
          setIrrigationTimes(times);

          const seriesData = createSeriesData(preparedData);

          setChartData({
            series: seriesData,
            absoluteCategories: preparedData.absoluteCategories,
            irrigationDetection: preparedData.irrigationDetection,
            rootsDetection: preparedData.rootsDetection,
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
        setIsInitialConfigLoaded(false);
      }
    };

    fetchData();
  }, [isInitialConfigLoaded, interval, selectedSector]);

  const customTooltip = useMemo(() => ({
    shared: true,
    useHTML: true,
    formatter(
      this: Highcharts.TooltipFormatterContextObject,
    ) {
      if (this.series.name === 'Comentarios') {
        return getConversationTooltip(this, conversations);
      }

      const header = '<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Resumen</div>';
      const subHeader = `<div style="font-size: 12px; margin-bottom: 8px;">${dayjs(this.x).format('MMM DD, YYYY')}</br></div>`;

      const currentDate = dayjs(this.x).format('YYYY-MM-DD');

      const irrigationPoint = this.points?.find((point) => point.series.name === 'Profundidad de riego (cm)' && point.point?.custom?.date === currentDate);
      const rootsPoint = this.points?.find((point) => point.series.name === 'Actividad de raíces (cm)' && point.point?.custom?.date === currentDate);

      let irrigationValue = irrigationPoint?.y;
      let rootsValue = rootsPoint?.y;

      if (irrigationValue === undefined || rootsValue === undefined) {
        const chart = this.points?.[0]?.series?.chart;
        if (chart) {
          chart.series.forEach((series: any) => {
            if (series.name === 'Profundidad de riego (cm)') {
              const point = series.points.find((p: any) => p.custom?.date === currentDate);
              if (point && irrigationValue === undefined) {
                irrigationValue = point.y;
              }
            }
            if (series.name === 'Actividad de raíces (cm)') {
              const point = series.points.find((p: any) => p.custom?.date === currentDate);
              if (point && rootsValue === undefined) {
                rootsValue = point.y;
              }
            }
          });
        }
      }

      let pointsHtml = '';

      if (irrigationValue !== null && irrigationValue !== undefined) {
        pointsHtml += `<div style="margin: 4px 0"><span style="color:#2374BF">\u25CF</span> Profundidad de riego (cm): <b>${Number(irrigationValue).toFixed(2)}</b></div>`;
      }

      if (rootsValue !== null && rootsValue !== undefined) {
        pointsHtml += `<div style="margin: 4px 0"><span style="color:#D2B48C">\u25CF</span> Actividad de raíces (cm): <b>${Number(rootsValue).toFixed(2)}</b></div>`;
      }

      return `<div style="padding: 8px">${header}${subHeader}${pointsHtml}</div>`;
    },
  }), [irrigationTimes, conversations]);

  const chartConfig = useMemo(() => interval && getRootsChartConfig(
    chartData,
    height,
    hideXValues,
    dayjs(interval?.startDate).valueOf(),
    dayjs(interval?.endDate).valueOf(),
    true,
    maxDetectionValue,
  ), [maxDetectionValue, interval, chartData.absoluteCategories]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(sectorId, ROOTS_NAME);
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  return (
    <>
      <Box>
        {isLoading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}
        <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          <BaseHighchart
            chartView={chartView}
            sectorId={sectorId}
            filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
            title={title}
            categories={chartData.categories}
            yAxisTitle={yAxisTitle}
            series={chartData.series as Highcharts.SeriesOptionsType[]}
            tooltip={customTooltip}
            showFullScreen={true}
            legend={{ enabled: !hideLegend }}
            chartConfig={chartConfig}
            hideXValues={hideXValues}
            onConfigClick={handleConfigClick}
            onFullScreenClick={handleFullScreenClick}
            hideYValues={hideYValues}
            setModalState={setModalState}
            conversations={conversations}
            absoluteCategories={chartData.absoluteCategories}
            height={height}
            zoomSync={true}
            autoAdjustYAxis={true}
            interval={interval}
            graphName={ROOTS_NAME}
          />
        </div>
      </Box>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={ROOTS_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Raíces"
      >
        <RootsConfigContent sectorId={sectorId} />
      </ChartConfigDialog>
    </>
  );
}

export default RootsChart;
